import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CelebrationIcon from '@mui/icons-material/Celebration';
import PlaceIcon from '@mui/icons-material/Place';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmptyState from '../common/EmptyState';
import { useSnackbar } from '../common/SnackbarProvider';
import swimService from '../../api/services/swimService';
import memberService from '../../api/services/memberService';
import {
  MyCalendarEntry,
  RSVPStatus,
  CompetitionAttendanceResponse,
  HouseholdSwimmerAttendance,
  MeetEvent,
  SwimCompetitionRow,
  SwimEventRow,
  EventHouseholdSwimmer,
} from '../../types/swim';
import { useIsMobile } from '../../hooks/useIsMobile';

interface Props {
  date: string;
  entries: MyCalendarEntry[];
  competitions: SwimCompetitionRow[];
  events: SwimEventRow[];
  onClose: () => void;
  onSaved: () => void;
}

function to12Hour(hhmm: string | null): string {
  if (!hhmm) return '';
  const [hStr, m] = hhmm.split(':');
  const h = Number(hStr);
  if (!Number.isFinite(h)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m ?? '00'} ${period}`;
}

function prettyDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

// Age brackets + open group (matches SwimMeetEvent_model). A swimmer sees their
// gender's events in their league-age group plus the open 18 & Under events.
const OPEN_GROUP = '18 & Under';
function ageGroupForAge(age: number | null): string | null {
  if (age == null) return null;
  if (age >= 19) return 'Adult';
  if (age <= 6) return '6 & Under';
  if (age <= 8) return '7-8';
  if (age <= 10) return '9-10';
  if (age <= 12) return '11-12';
  if (age <= 14) return '13-14';
  if (age <= 16) return '15-16';
  return '17-18';
}

// Mirrors api/models/SwimMeetEvent_model.py:MAX_INDIVIDUAL_EVENTS_PER_MEET.
// Rec-league rule: up to 3 individual events per meet; relays don't count.
const MAX_INDIVIDUAL_EVENTS = 3;

const ParentDayDetailDialog: React.FC<Props> = ({ date, entries, competitions, events, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const snackbar = useSnackbar();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [localEntries, setLocalEntries] = useState<MyCalendarEntry[]>(entries);

  // Per-competition attendance state. We load eagerly when the dialog opens
  // and any competitions exist on this date.
  const [attendanceByComp, setAttendanceByComp] = useState<Record<string, CompetitionAttendanceResponse>>({});
  const [meetEvents, setMeetEvents] = useState<MeetEvent[]>([]);
  const [loadingMeet, setLoadingMeet] = useState(false);
  // memberIds currently mid-save (per swimmer spinner)
  const [savingMember, setSavingMember] = useState<Set<string>>(new Set());
  const [eventAttByEvent, setEventAttByEvent] = useState<Record<string, EventHouseholdSwimmer[]>>({});

  const dailyWorkout = localEntries[0]?.daily_workout ?? null;
  const hasCompetitions = competitions.length > 0;

  useEffect(() => {
    if (!hasCompetitions) return;
    setLoadingMeet(true);
    void (async () => {
      try {
        const [events, ...rows] = await Promise.all([
          swimService.listMeetEvents(),
          ...competitions.map((c) => swimService.getCompetitionAttendance(c.id)),
        ]);
        setMeetEvents(events);
        const map: Record<string, CompetitionAttendanceResponse> = {};
        rows.forEach((row, i) => {
          map[competitions[i].id] = row;
        });
        setAttendanceByComp(map);
      } catch (e: unknown) {
        type ErrLike = { response?: { data?: { error?: string } }; message?: string };
        const err = e as ErrLike;
        snackbar.error(err?.response?.data?.error || err?.message || 'Failed to load meet info');
      } finally {
        setLoadingMeet(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load household RSVP state for any club events on this date (parent view).
  useEffect(() => {
    if (events.length === 0) return;
    void (async () => {
      try {
        const rows = await Promise.all(
          events.map((ev) => swimService.getEventHouseholdAttendance(ev.id)),
        );
        const map: Record<string, EventHouseholdSwimmer[]> = {};
        rows.forEach((swimmers, i) => {
          map[events[i].id] = swimmers;
        });
        setEventAttByEvent(map);
      } catch (e: unknown) {
        type ErrLike = { response?: { data?: { error?: string } }; message?: string };
        const err = e as ErrLike;
        snackbar.error(err?.response?.data?.error || err?.message || 'Failed to load event RSVPs');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggle(entry: MyCalendarEntry) {
    const next: RSVPStatus = entry.rsvp_status === 'skip' ? 'planned' : 'skip';
    const key = `${entry.enrollment_id}|${entry.session_id}`;
    setBusyKey(key);
    try {
      await swimService.setRSVP(entry.enrollment_id, entry.session_id, next);
      setLocalEntries((prev) =>
        prev.map((e) =>
          e.enrollment_id === entry.enrollment_id && e.session_id === entry.session_id
            ? { ...e, rsvp_status: next }
            : e,
        ),
      );
      onSaved();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Failed to update');
    } finally {
      setBusyKey(null);
    }
  }

  async function updateAttendance(
    compId: string,
    swimmer: HouseholdSwimmerAttendance,
    attending: boolean,
    eventCodes: string[],
  ) {
    const key = swimmer.id ?? swimmer.member_id ?? String(swimmer.non_member_dependent_id ?? '');
    setSavingMember((s) => new Set(s).add(key));
    try {
      const result = await swimService.setCompetitionAttendance(
        compId,
        { member_id: swimmer.member_id, non_member_dependent_id: swimmer.non_member_dependent_id },
        attending,
        eventCodes,
      );
      setAttendanceByComp((prev) => {
        const next = { ...prev };
        const comp = next[compId];
        if (!comp) return prev;
        const sk = (s: HouseholdSwimmerAttendance) => s.id ?? s.member_id ?? String(s.non_member_dependent_id ?? '');
        next[compId] = {
          ...comp,
          swimmers: comp.swimmers.map((s) =>
            sk(s) === key
              ? { ...s, attending: result.attending, event_codes: result.event_codes }
              : s,
          ),
        };
        return next;
      });
      onSaved();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Failed to update attendance');
    } finally {
      setSavingMember((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  }

  async function setSwimmerGender(
    compId: string,
    swimmer: HouseholdSwimmerAttendance,
    gender: 'female' | 'male',
  ) {
    const memberId = swimmer.member_id;
    if (!memberId) return;  // gender only applies to members (non-member deps have none)
    setSavingMember((s) => new Set(s).add(memberId));
    try {
      await memberService.setMemberGender(memberId, gender);
      const label = gender === 'female' ? 'Girls' : 'Boys';
      setAttendanceByComp((prev) => {
        const next = { ...prev };
        const comp = next[compId];
        if (!comp) return prev;
        next[compId] = {
          ...comp,
          swimmers: comp.swimmers.map((s) =>
            s.member_id === memberId ? { ...s, gender: label } : s,
          ),
        };
        return next;
      });
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Failed to set gender');
    } finally {
      setSavingMember((s) => {
        const next = new Set(s);
        next.delete(memberId);
        return next;
      });
    }
  }

  function renderEventPicker(
    compId: string,
    swimmer: HouseholdSwimmerAttendance,
  ) {
    const codes = swimmer.event_codes;
    const group = ageGroupForAge(swimmer.age);
    const g = swimmer.gender ?? null;
    // Events this swimmer may enter: their gender, in their league-age group
    // or the open 18 & Under group.
    const available = meetEvents.filter(
      (e) => (!g || e.gender === g) && (e.age_group === OPEN_GROUP || e.age_group === group),
    );
    const availCodes = new Set(available.map((e) => e.code));
    const shown = [
      ...available,
      ...meetEvents.filter((e) => codes.includes(e.code) && !availCodes.has(e.code)),
    ].sort(
      (a, b) =>
        a.age_group_order - b.age_group_order ||
        (a.distance_yards ?? 0) - (b.distance_yards ?? 0) ||
        a.label.localeCompare(b.label),
    );
    const atLimit = codes.length >= MAX_INDIVIDUAL_EVENTS;
    const swimmerKey = swimmer.id ?? swimmer.member_id ?? String(swimmer.non_member_dependent_id ?? '');
    const saving = savingMember.has(swimmerKey);

    return (
      <Stack spacing={1} sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Pick up to {MAX_INDIVIDUAL_EVENTS} events
          {[g, group].filter(Boolean).length ? ` (${[g, group].filter(Boolean).join(' · ')})` : ''}
          {' '}— {codes.length}/{MAX_INDIVIDUAL_EVENTS} selected, relays don't count.
        </Typography>
        {!g && swimmer.member_id && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="caption" color="warning.main">No gender set —</Typography>
            <Button size="small" variant="outlined" disabled={saving} onClick={() => void setSwimmerGender(compId, swimmer, 'female')}>Female</Button>
            <Button size="small" variant="outlined" disabled={saving} onClick={() => void setSwimmerGender(compId, swimmer, 'male')}>Male</Button>
          </Stack>
        )}
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {shown.map((evt) => {
            const selected = codes.includes(evt.code);
            const disabled = saving || (atLimit && !selected);
            return (
              <Chip
                key={evt.code}
                size="small"
                label={evt.label}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() => {
                  const next = selected
                    ? codes.filter((c) => c !== evt.code)
                    : [...codes, evt.code];
                  void updateAttendance(compId, swimmer, true, next);
                }}
                disabled={disabled}
              />
            );
          })}
        </Stack>
      </Stack>
    );
  }

  async function toggleEventRsvp(eventId: string, swimmer: EventHouseholdSwimmer, attending: boolean) {
    const swimmerKey = swimmer.id ?? swimmer.member_id ?? String(swimmer.non_member_dependent_id ?? '');
    const key = `evt:${eventId}:${swimmerKey}`;
    setSavingMember((s) => new Set(s).add(key));
    try {
      await swimService.setEventAttendance(
        eventId,
        { member_id: swimmer.member_id, non_member_dependent_id: swimmer.non_member_dependent_id },
        attending,
      );
      setEventAttByEvent((prev) => {
        const list = prev[eventId];
        if (!list) return prev;
        const sk = (x: EventHouseholdSwimmer) => x.id ?? x.member_id ?? String(x.non_member_dependent_id ?? '');
        return {
          ...prev,
          [eventId]: list.map((x) => (sk(x) === swimmerKey ? { ...x, attending } : x)),
        };
      });
      onSaved();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Failed to update RSVP');
    } finally {
      setSavingMember((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  }

  function renderEventRsvp(eventId: string) {
    const swimmers = eventAttByEvent[eventId];
    if (!swimmers || swimmers.length === 0) return null;
    return (
      <Box sx={{ mt: 1 }}>
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary">Who's going?</Typography>
        <Stack sx={{ mt: 0.25 }}>
          {swimmers.map((s) => {
            const swimmerKey = s.id ?? s.member_id ?? String(s.non_member_dependent_id ?? '');
            const saving = savingMember.has(`evt:${eventId}:${swimmerKey}`);
            return (
              <Stack key={swimmerKey} direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  size="small"
                  checked={s.attending}
                  disabled={saving}
                  onChange={(e) => void toggleEventRsvp(eventId, s, e.target.checked)}
                  sx={{ p: 0.5 }}
                />
                <Typography variant="body2">{s.first_name} {s.last_name}</Typography>
                {saving && <CircularProgress size={12} />}
              </Stack>
            );
          })}
        </Stack>
      </Box>
    );
  }

  function renderCompetition(comp: typeof competitions[number]) {
    const att = attendanceByComp[comp.id];
    return (
      <Box
        key={comp.id}
        sx={(theme) => ({
          mb: 2,
          p: 2,
          backgroundColor: alpha(theme.palette.warning.main, 0.08),
          borderRadius: 2,
        })}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <EmojiEventsIcon fontSize="small" sx={{ color: 'warning.dark' }} />
          <Typography variant="subtitle1" sx={{ color: 'warning.dark', fontWeight: 600 }}>
            {comp.name}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {[
            comp.location,
            comp.start_time ? to12Hour(comp.start_time) : null,
            comp.host_team ? `hosted by ${comp.host_team}` : null,
          ].filter(Boolean).join(' · ')}
        </Typography>
        {comp.notes && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {comp.notes}
          </Typography>
        )}
        {comp.league && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.dark' }}>
            {comp.league.name}
            {comp.league.age_up_date ? ` · age groups as of ${prettyDate(comp.league.age_up_date)}` : ''}
          </Typography>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Who's going?</Typography>
          {loadingMeet && <CircularProgress size={14} />}
        </Stack>

        {!att || att.swimmers.length === 0 ? (
          loadingMeet ? null : (
            <Typography variant="caption" color="text.secondary">
              No swimmers on your account.
            </Typography>
          )
        ) : (
          <Stack spacing={1.5}>
            {att.swimmers.map((swimmer) => {
              const ageLabel =
                swimmer.age != null
                  ? `age ${swimmer.age}`
                  : swimmer.member_type === 'DEPENDENT'
                  ? 'dependent'
                  : '';
              const swimmerKey = swimmer.id ?? swimmer.member_id ?? String(swimmer.non_member_dependent_id ?? '');
              const saving = savingMember.has(swimmerKey);
              return (
                <Box key={swimmerKey}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={swimmer.attending}
                      onChange={(e) => {
                        void updateAttendance(
                          comp.id,
                          swimmer,
                          e.target.checked,
                          swimmer.event_codes,
                        );
                      }}
                      disabled={saving}
                      sx={{ p: 0.5 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {swimmer.first_name} {swimmer.last_name}
                        {ageLabel && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({ageLabel})
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                    {saving && <CircularProgress size={14} />}
                  </Stack>
                  {swimmer.attending && renderEventPicker(comp.id, swimmer)}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{prettyDate(date)}</DialogTitle>
      <DialogContent dividers>
        {hasCompetitions && (
          <Box>
            {competitions.map((c) => renderCompetition(c))}
          </Box>
        )}

        {events.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
              <CelebrationIcon fontSize="small" sx={{ color: 'secondary.main' }} />
              <Typography variant="subtitle2" sx={{ color: 'secondary.main' }}>
                Events
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {events.map((ev) => (
                <Box
                  key={ev.id}
                  sx={(theme) => ({
                    p: 1.5,
                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                    borderRadius: 2,
                  })}
                >
                  <Typography variant="subtitle2">{ev.name}</Typography>
                  {(ev.start_time || ev.end_time) && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                      <AccessTimeIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {to12Hour(ev.start_time)}
                        {ev.end_time ? ` – ${to12Hour(ev.end_time)}` : ''}
                      </Typography>
                    </Stack>
                  )}
                  {ev.location && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                      <PlaceIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {ev.location}
                      </Typography>
                    </Stack>
                  )}
                  {ev.notes && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, whiteSpace: 'pre-wrap', color: 'text.secondary' }}
                    >
                      {ev.notes}
                    </Typography>
                  )}
                  {renderEventRsvp(ev.id)}
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {dailyWorkout && (
          <Box
            sx={(theme) => ({
              mb: 2,
              p: 1.75,
              backgroundColor: alpha(theme.palette.success.main, 0.08),
              borderRadius: 2,
            })}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
              <EventNoteIcon fontSize="small" sx={{ color: 'success.dark' }} />
              <Typography variant="subtitle2" sx={{ color: 'success.dark' }}>
                Today's Workout
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{dailyWorkout}</Typography>
          </Box>
        )}

        {localEntries.length === 0 ? (
          hasCompetitions || events.length > 0 ? null : (
            <EmptyState
              size="sm"
              icon={<CalendarTodayIcon />}
              title="No sessions today"
              body="None of your swimmers have a class scheduled for this date."
            />
          )
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Your swimmers</Typography>
            {localEntries.map((e) => {
              const key = `${e.enrollment_id}|${e.session_id}`;
              const isCancelled = e.is_cancelled;
              const isSkip = e.rsvp_status === 'skip';
              // If this swimmer is marked attending any of today's
              // competitions, lock out the practice toggle — they can't be
              // in two places at once. To re-enable the practice toggle
              // they uncheck the meet attendance.
              const atMeet = e.swimmer_member_id
                ? Object.values(attendanceByComp).some((row) =>
                    row.swimmers.some(
                      (s) => s.member_id === e.swimmer_member_id && s.attending,
                    ),
                  )
                : false;
              return (
                <Box
                  key={key}
                  sx={{
                    borderLeft: 3,
                    borderColor: isCancelled
                      ? 'error.main'
                      : atMeet
                      ? 'warning.main'
                      : isSkip
                      ? 'grey.500'
                      : 'success.main',
                    pl: 1.5,
                    py: 0.5,
                    opacity: isCancelled || isSkip || atMeet ? 0.7 : 1,
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={1}
                  >
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{ textDecoration: isCancelled ? 'line-through' : 'none' }}
                      >
                        <strong>{e.swimmer_name}</strong>{' '}
                        <Typography component="span" variant="body2" color="text.secondary">
                          • {to12Hour(e.start_time)} – {to12Hour(e.end_time)}
                        </Typography>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {e.program_name}
                      </Typography>
                      {isCancelled && e.cancellation_reason && (
                        <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                          Cancelled: {e.cancellation_reason}
                        </Typography>
                      )}
                    </Box>
                    {isCancelled ? (
                      <Chip size="small" color="error" label="Cancelled" />
                    ) : atMeet ? (
                      <Chip
                        size="small"
                        icon={<EmojiEventsIcon fontSize="small" />}
                        label="At meet"
                        color="warning"
                      />
                    ) : (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          icon={isSkip ? <DoNotDisturbIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                          label={isSkip ? 'Skipping' : 'Planned'}
                          color={isSkip ? 'default' : 'success'}
                          variant={isSkip ? 'outlined' : 'filled'}
                        />
                        <Button
                          size="small"
                          variant={isSkip ? 'contained' : 'outlined'}
                          onClick={() => void toggle(e)}
                          disabled={busyKey === key}
                        >
                          {busyKey === key ? '…' : isSkip ? "I'll come" : "I'll skip"}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                  {!isCancelled && e.workout && e.workout !== dailyWorkout && (
                    <>
                      <Divider sx={{ my: 0.75 }} />
                      <Typography variant="caption" color="text.secondary">
                        <strong>Class-specific workout:</strong> {e.workout}
                      </Typography>
                    </>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParentDayDetailDialog;
